import { useState } from 'react';
import {
    ArrowRightIcon,
    ChevronDownIcon,
    PencilSquareIcon,
    PhoneArrowDownLeftIcon,
    PhoneArrowUpRightIcon
} from '@heroicons/react/24/outline';
import { formatRelative, formatShortDate } from '../../../patient-files/timeline-rail/event-time';
import type {
    PracticeTimelineChange,
    PracticeTimelineEntry
} from '../../../../services/practiceTimeline';

// The Details tab aside's third card - the "Recent edits" slot the design
// called for and DetailAside left unbuilt for want of a practice-activity
// endpoint. Now that GET /practices/:id/timeline exists it is filled, and
// widened past edits to the merged feed: practice changes and logged calls
// are the same story about the account.
//
// Folded cards on a hairline rail, the same shape as billing's EventTimeline:
// collapsed, an entry is one line of "what happened, when"; expanded, a
// practice edit shows every field it touched with the value it replaced. The
// old value is the whole point of an audit trail, and at 268px it cannot be
// shown inline for every entry at once - so it lives one click away instead
// of being dropped.

/** Page size for the aside's feed - exported so the fetch in DetailAside and
 *  this card's layout assumptions stay one number. */
export const PRACTICE_ACTIVITY_ENTRIES = 15;

function EmptyValue() {
    return <span className="text-gray-300 italic">empty</span>;
}

// Label above, then "old → new" reading left to right. The pair wraps rather
// than truncating: at 268px a long value (an address, an email) would lose
// its tail to an ellipsis, and the tail is often the part that changed.
function ChangeRow({ change }: { change: PracticeTimelineChange }) {
    return (
        <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[11px] text-gray-400">{change.label}</span>
            <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 min-w-0">
                <span className="text-[11px] text-gray-500 break-words">
                    {change.before ?? <EmptyValue />}
                </span>
                <ArrowRightIcon className="h-2.5 w-2.5 text-gray-400 shrink-0 self-center" />
                <span className="text-[12px] text-gray-800 font-medium break-words">
                    {change.after ?? <EmptyValue />}
                </span>
            </div>
        </div>
    );
}

function CallDetail({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-baseline justify-between gap-2 min-w-0">
            <span className="text-[11px] text-gray-400 shrink-0">{label}</span>
            <span className="text-[11px] text-gray-700 font-medium text-right break-all">
                {value}
            </span>
        </div>
    );
}

function ExpandedBody({ entry }: { entry: PracticeTimelineEntry }) {
    if (entry.kind === 'call') {
        const call = entry.call;
        if (!call) return null;
        return (
            <div className="flex flex-col gap-1.5">
                <CallDetail label="Reference" value={call.referenceNo} />
                {call.callerName && <CallDetail label="Caller" value={call.callerName} />}
                <CallDetail label="Status" value={call.status} />
                {call.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-0.5">
                        {call.categories.map((category) => (
                            <span
                                key={category}
                                className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600"
                            >
                                {category}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-2">
            {(entry.changes ?? []).map((change) => (
                <ChangeRow key={change.field} change={change} />
            ))}
        </div>
    );
}

interface EntryVisual {
    icon: typeof PencilSquareIcon;
    ring: string;
    iconColor: string;
    title: string;
    /** The collapsed one-liner: enough to skip an entry without opening it. */
    summary: string;
}

function entryVisual(entry: PracticeTimelineEntry): EntryVisual {
    if (entry.kind === 'call') {
        const isIncoming = entry.subtype === 'incoming';
        return {
            icon: isIncoming ? PhoneArrowDownLeftIcon : PhoneArrowUpRightIcon,
            ring: 'border-teal-300 bg-teal-50',
            iconColor: 'text-[#2C6F67]',
            title: isIncoming ? 'Incoming call' : 'Outgoing call',
            summary: entry.call?.title ?? ''
        };
    }
    const changes = entry.changes ?? [];
    // One field names itself; several would truncate, so they get a count and
    // the detail is one click away. Zero is reachable when a stored payload
    // fails the reader's defensive parse - "0 fields changed" would be a lie,
    // so it says nothing rather than a number it cannot back up.
    const summary =
        changes.length === 0
            ? ''
            : changes.length === 1
              ? changes[0].label
              : `${changes.length} fields changed`;
    return {
        icon: PencilSquareIcon,
        ring: 'border-gray-300 bg-white',
        iconColor: 'text-gray-600',
        title: 'Details updated',
        summary
    };
}

function TimelineCard({ entry, isLast }: { entry: PracticeTimelineEntry; isLast: boolean }) {
    const [expanded, setExpanded] = useState(false);
    const visual = entryVisual(entry);
    const Icon = visual.icon;
    const relative = formatRelative(entry.occurredAt);

    // A practice edit with no recorded fields, or a call whose payload failed
    // to parse, has nothing behind the fold - so it does not offer one.
    const expandable =
        entry.kind === 'call' ? entry.call !== null : (entry.changes ?? []).length > 0;

    return (
        <li className="relative pl-6 pb-2 last:pb-0">
            {!isLast && <span className="absolute left-[9px] top-5 bottom-0 w-px bg-gray-200" />}
            <span
                className={`absolute left-[1px] top-1.5 w-[17px] h-[17px] rounded-full border ${visual.ring} flex items-center justify-center ${visual.iconColor}`}
            >
                <Icon className="w-2.5 h-2.5" />
            </span>
            <div className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 min-w-0">
                {/* Only the header toggles - an expanded diff stays a plain,
                    selectable region so a value can be copied out without the
                    card collapsing under the cursor. */}
                <div
                    className={expandable ? 'cursor-pointer select-none' : undefined}
                    onClick={expandable ? () => setExpanded((v) => !v) : undefined}
                    onKeyDown={
                        expandable
                            ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setExpanded((v) => !v);
                                  }
                              }
                            : undefined
                    }
                    role={expandable ? 'button' : undefined}
                    tabIndex={expandable ? 0 : undefined}
                    aria-expanded={expandable ? expanded : undefined}
                >
                    <div className="flex items-baseline justify-between gap-1.5 min-w-0">
                        <span className="text-[12px] font-semibold text-gray-900 truncate">
                            {visual.title}
                        </span>
                        <div className="flex items-center gap-1 shrink-0">
                            {relative && (
                                <span className="text-[10px] text-gray-400">{relative}</span>
                            )}
                            {expandable && (
                                <ChevronDownIcon
                                    className={`w-3 h-3 text-gray-400 transition-transform duration-150 ${
                                        expanded ? 'rotate-180' : ''
                                    }`}
                                />
                            )}
                        </div>
                    </div>
                    {visual.summary && (
                        <p className="text-[11px] text-gray-500 truncate">{visual.summary}</p>
                    )}
                </div>

                {expandable && expanded && (
                    <div className="mt-1.5 pt-1.5 border-t border-gray-100">
                        <ExpandedBody entry={entry} />
                    </div>
                )}

                <p className="text-[10px] text-gray-400 truncate mt-1">
                    {entry.actorName ?? 'System'}
                    {formatShortDate(entry.occurredAt)
                        ? ` · ${formatShortDate(entry.occurredAt)}`
                        : ''}
                </p>
            </div>
        </li>
    );
}

/**
 * Presentational: the fetch lives in DetailAside, which needs to know whether
 * this card will render anything before it decides to draw a 268px gutter at
 * all. Rendering nothing for an empty feed stays here, so the two cannot
 * disagree about what "empty" means.
 */
export function PracticeActivityCard({ entries }: { entries: PracticeTimelineEntry[] }) {
    if (entries.length === 0) return null;

    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)] px-4 py-3.5 flex flex-col gap-3">
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.06em]">
                Recent activity
            </span>
            <ul className="flex flex-col">
                {entries.map((entry, i) => (
                    <TimelineCard
                        key={`${entry.kind}-${entry.id}`}
                        entry={entry}
                        isLast={i === entries.length - 1}
                    />
                ))}
            </ul>
        </div>
    );
}
